import { useEffect } from 'react';
import { create } from 'zustand';
import { countPending, drain } from '@kapture/sync-core';
import { isPlatformConfigured, platform } from '@kapture/platform';

interface SyncState {
  online: boolean;
  pending: number;
  lastDrainAt: string | null;
  setOnline(online: boolean): void;
  refresh(): Promise<void>;
}

/**
 * Tiny Zustand store for UI state around connectivity and the queue size.
 * Drives the header badge and any per-screen "X queued" pills.
 *
 * It intentionally does NOT hold captures, sessions, or forms — those are
 * server/DB state and live in TanStack Query + SQLite.
 */
export const useSyncStore = create<SyncState>((set, get) => ({
  // `create()` runs at module evaluation — before `configurePlatform()` on any
  // import order where a module reaches this store on its way up. Reading the
  // registry unguarded here throws during import, which is both a real crash
  // and the kind that moves with bundler resolution order rather than with
  // anything you changed. Optimistic default; `useSyncStatus`'s subscription
  // is the source of truth from the first render onward.
  online: isPlatformConfigured() ? platform().net.isOnline() : true,
  pending: 0,
  lastDrainAt: null,
  setOnline(online) {
    const wasOffline = !get().online;
    set({ online });
    if (online && wasOffline) {
      void drain()
        .then(() => get().refresh())
        .then(() => set({ lastDrainAt: new Date().toISOString() }));
    }
  },
  async refresh() {
    try {
      const n = await countPending();
      set({ pending: n });
    } catch {
      // DB not ready yet (e.g. showcase page loaded pre-init). Safe to skip.
    }
  },
}));

/**
 * Convenience hook: binds the store to the platform's connectivity signal and
 * returns the current snapshot. Use this in headers and sync chrome.
 *
 * The browser's `online`/`offline` events and NetInfo's subscription are the
 * same shape behind `NetworkMonitor.subscribe`, so this hook is identical on
 * both platforms.
 */
export function useSyncStatus() {
  const state = useSyncStore();
  useEffect(() => {
    const unsubscribe = platform().net.subscribe((online) => {
      useSyncStore.getState().setOnline(online);
    });
    void state.refresh();
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { online: state.online, pending: state.pending };
}
